/**
 * My In-Progress Books Component
 * 
 * Shows scanned_books that still need agent work. Catches proactively
 * scanned books that don't have a book_scan_request.
 * 
 * Displays books where:
 * - quality_status NOT IN ('ready', 'turn_codes_pending')
 * - contributor_id matches current user (security: only show your own books)
 * 
 * Books in 'turn_codes_pending' are excluded because they've been published
 * and are awaiting admin turn-code review — the agent has no action to take.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Clock,
  Edit3,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Zap,
  Trash2,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { glassStyles } from '@/lib/design';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';
import { triggerBackgroundProcessing } from '@/services/scannedBookService';

// ============================================================================
// Types
// ============================================================================

interface InProgressBook {
  id: string;
  title: string;
  author: string | null;
  cover_image_url: string | null;
  quality_status: 'pending' | 'processing' | 'reconstructing' | 'turn_codes_pending' | 'needs_review' | 'failed' | 'ready' | null;
  scan_completed: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  page_count?: number;
  processed_count?: number;
}

interface MyInProgressBooksProps {
  onRefresh?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function MyInProgressBooks({ onRefresh }: MyInProgressBooksProps) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const [books, setBooks] = useState<InProgressBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  const fetchBooks = useCallback(async () => {
    if (!profile?.id) return;
    
    try {
      // Fetch scanned_books that still need agent work.
      // Excludes 'ready' (published) and 'turn_codes_pending' (awaiting admin
      // turn-code review — agent has no action to take on these).
      const { data: booksData, error } = await supabase
        .from('scanned_books')
        .select(`
          id,
          title,
          author,
          cover_image_url,
          quality_status,
          scan_completed,
          created_at,
          updated_at
        `)
        .eq('contributor_id', profile.id)
        .not('quality_status', 'in', '("ready","turn_codes_pending")')
        .is('deleted_at', null)
        .order('updated_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      
      if (!booksData || booksData.length === 0) {
        setBooks([]);
        return;
      }

      // Get page counts for each book
      const bookIds = booksData.map(b => b.id);
      const { data: pageCounts } = await supabase
        .from('scanned_book_pages')
        .select('book_id, processing_status')
        .in('book_id', bookIds);

      // Calculate page stats per book
      const pageStats = new Map<string, { total: number; processed: number }>();
      (pageCounts || []).forEach(page => {
        const stats = pageStats.get(page.book_id) || { total: 0, processed: 0 };
        stats.total++;
        if (page.processing_status === 'complete') {
          stats.processed++;
        }
        pageStats.set(page.book_id, stats);
      });

      // Enrich books with page counts
      const enrichedBooks: InProgressBook[] = booksData.map(book => ({
        ...book,
        page_count: pageStats.get(book.id)?.total || 0,
        processed_count: pageStats.get(book.id)?.processed || 0,
      }));

      setBooks(enrichedBooks);
    } catch (err) {
      console.error('[MyInProgressBooks] Error fetching:', err);
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'fetch_books' } });
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  // ============================================================================
  // Delete Handler
  // ============================================================================
  
  const handleDeleteBook = useCallback(async (bookId: string) => {
    if (!profile?.id) return;
    
    setDeletingId(bookId);
    try {
      // Soft-delete: set deleted_at timestamp instead of hard deleting
      const { error: bookError } = await supabase
        .from('scanned_books')
        .update({ deleted_at: new Date().toISOString() } as any)
        .eq('id', bookId)
        .eq('contributor_id', profile.id);
      
      if (bookError) throw bookError;
      
      // Remove from local state
      setBooks(prev => prev.filter(b => b.id !== bookId));
      setConfirmDeleteId(null);
      onRefresh?.();
    } catch (err) {
      console.error('[MyInProgressBooks] Error deleting book:', err);
      toast.error('Failed to delete book. Please try again.');
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'delete_book' }, extra: { bookId } });
    } finally {
      setDeletingId(null);
    }
  }, [profile?.id, onRefresh]);

  // ============================================================================
  // Retry Handler - for failed books with error pages
  // ============================================================================
  
  const handleRetryProcessing = useCallback(async (bookId: string) => {
    if (!profile?.id) return;
    
    setRetryingId(bookId);
    try {
      // First, reset any error pages to pending
      // RLS will ensure we can only update pages for books we own
      const { error: resetError } = await supabase
        .from('scanned_book_pages')
        .update({ 
          processing_status: 'pending',
          quality_issues: null 
        } as any)
        .eq('book_id', bookId)
        .eq('processing_status', 'error');
      
      if (resetError) {
        console.error('[MyInProgressBooks] Error resetting pages:', resetError);
        throw resetError;
      }
      
      // Reset book status to processing
      // Security: double-check ownership with contributor_id
      const { error: bookError } = await supabase
        .from('scanned_books')
        .update({ 
          quality_status: 'processing',
          reconstructed_text: null,
          quality_issues: null 
        } as any)
        .eq('id', bookId)
        .eq('contributor_id', profile.id); // Security: verify ownership
      
      if (bookError) {
        console.error('[MyInProgressBooks] Error resetting book:', bookError);
        throw bookError;
      }
      
      // Trigger background processing
      console.log(`[MyInProgressBooks] Triggering retry for book ${bookId}`);
      const result = await triggerBackgroundProcessing(bookId);
      
      if (!result.success) {
        console.error('[MyInProgressBooks] Retry failed:', result.error);
        // Revert status to 'failed' so retry button remains visible
        await supabase
          .from('scanned_books')
          .update({ quality_status: 'failed' } as any)
          .eq('id', bookId)
          .eq('contributor_id', profile.id);
      }
      
      // Refresh the list
      await fetchBooks();
      onRefresh?.();
    } catch (err) {
      console.error('[MyInProgressBooks] Error retrying:', err);
      // Revert status to 'failed' so retry button remains visible
      await supabase
        .from('scanned_books')
        .update({ quality_status: 'failed' } as any)
        .eq('id', bookId)
        .eq('contributor_id', profile?.id);
      await fetchBooks();
      toast.error('Failed to retry processing. Please try again.');
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'retry_processing' }, extra: { bookId } });
    } finally {
      setRetryingId(null);
    }
  }, [profile?.id, fetchBooks, onRefresh]);
  
  // Initial fetch
  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  // Poll for updates every 30 seconds (processing books may change status)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBooks();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchBooks]);

  // ============================================================================
  // Helpers
  // ============================================================================
  
  const formatTimeAgo = (timestamp: string | null): string => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  
  const getStatusInfo = (book: InProgressBook) => {
    // Check if all pages are processed (for determining edit availability)
    const allPagesProcessed = book.page_count && 
      book.page_count > 0 &&
      book.processed_count !== undefined && 
      book.processed_count >= book.page_count;

    // Check if still processing pages
    if (book.page_count && book.processed_count !== undefined && book.processed_count < book.page_count) {
      return {
        icon: <Loader2 className="w-4 h-4 animate-spin" />,
        bg: 'bg-[#5E5CE6]/15',
        text: 'text-[#5E5CE6]',
        label: `Processing ${book.processed_count}/${book.page_count}`,
        canEdit: false,
      };
    }

    switch (book.quality_status) {
      case 'pending':
        return {
          icon: <Clock className="w-4 h-4" />,
          bg: 'bg-[#FF9F0A]/15',
          text: 'text-[#FF9F0A]',
          label: 'Pending',
          canEdit: false,
        };
      case 'processing':
      case 'reconstructing':
        // Group processing and reconstructing - both are "still working"
        return {
          icon: <Loader2 className="w-4 h-4 animate-spin" />,
          bg: 'bg-[#5E5CE6]/15',
          text: 'text-[#5E5CE6]',
          label: 'Processing',
          canEdit: false,
        };
      case 'needs_review':
        // OCR done, ready for human editing
        return {
          icon: <Edit3 className="w-4 h-4" />,
          bg: 'bg-[#30D158]/15',
          text: 'text-[#30D158]',
          label: 'Ready to Edit',
          canEdit: true,
        };
      case 'failed':
        // Failed but pages may still have extractable text
        return {
          icon: allPagesProcessed ? <Edit3 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />,
          bg: allPagesProcessed ? 'bg-[#30D158]/15' : 'bg-[#FF453A]/15',
          text: allPagesProcessed ? 'text-[#30D158]' : 'text-[#FF453A]',
          label: allPagesProcessed ? 'Ready to Edit' : 'Failed',
          canEdit: !!allPagesProcessed,
        };
      default:
        // Unknown status (including null) - show as unknown, don't allow edit
        return {
          icon: <Clock className="w-4 h-4" />,
          bg: 'bg-black/[0.05]',
          text: 'text-[#636366]',
          label: book.quality_status || 'Unknown',
          canEdit: false,
        };
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  
  // Don't show section if no books
  if (!loading && books.length === 0) {
    return null;
  }
  
  if (loading) {
    return (
      <div className="mb-6 p-4 rounded-2xl" style={glassStyles.card}>
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-[#5E5CE6] animate-spin" />
          <span className="text-[#8E8E93] text-sm">Loading your books...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mb-6 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#5E5CE6]" />
          <span className="text-sm font-medium text-[#1C1C1E]">My Books</span>
          <span className="text-xs text-[#8E8E93]">
            ({books.length} in progress)
          </span>
        </div>
        <button
          onClick={() => {
            setLoading(true);
            fetchBooks().finally(() => setLoading(false));
            onRefresh?.();
          }}
          className="p-1.5 rounded-lg hover:bg-black/[0.05] transition-colors text-[#8E8E93] hover:text-[#1C1C1E]"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      
      {/* Book Cards */}
      {books.map((book) => {
        const statusInfo = getStatusInfo(book);
        
        return (
          <div
            key={book.id}
            className="p-4 rounded-2xl border border-[#5E5CE6]/20 bg-[#5E5CE6]/5"
          >
            <div className="flex items-start gap-4">
              {/* Cover */}
              <div className="w-14 h-18 bg-black/[0.03] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {book.cover_image_url ? (
                  <img
                    src={book.cover_image_url}
                    alt={book.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <BookOpen className="w-5 h-5 text-[#AEAEB2]" />
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-medium text-[#1C1C1E] truncate">{book.title}</h3>
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                    {statusInfo.icon}
                    {statusInfo.label}
                  </span>
                </div>
                
                {book.author && (
                  <p className="text-sm text-[#8E8E93] mb-2 truncate">{book.author}</p>
                )}
                
                {/* Activity Info */}
                <div className="flex items-center gap-3 text-xs text-[#8E8E93] mb-3">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Updated {formatTimeAgo(book.updated_at)}
                  </span>
                  {book.page_count !== undefined && book.page_count > 0 && (
                    <span>
                      {book.page_count} page{book.page_count !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  {statusInfo.canEdit ? (
                    <button
                      onClick={() => navigate(`/scan-agent/edit/${book.id}`)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-[#30D158] text-[#1A1A1A] hover:brightness-110"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Content
                    </button>
                  ) : (book.quality_status === 'failed' || 
                        // Also show retry for stuck processing books (has pages but 0 processed)
                        (book.quality_status === 'processing' && book.page_count && book.page_count > 0 && book.processed_count === 0)) ? (
                    // Failed or stuck books with pages can be retried
                    <button
                      onClick={() => handleRetryProcessing(book.id)}
                      disabled={retryingId === book.id}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all bg-[#FF9F0A] text-[#1A1A1A] hover:brightness-110 disabled:opacity-50"
                    >
                      {retryingId === book.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      {retryingId === book.id ? 'Retrying...' : 'Retry Processing'}
                    </button>
                  ) : (
                    <span className="text-xs text-[#8E8E93]">
                      {book.page_count === 0 || book.page_count === undefined
                        ? 'No pages uploaded yet'
                        : 'Processing text... check back shortly'}
                    </span>
                  )}
                  
                  {/* Delete Button - with confirmation */}
                  {confirmDeleteId === book.id ? (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-[#FF453A] mr-1">Delete?</span>
                      <button
                        onClick={() => handleDeleteBook(book.id)}
                        disabled={deletingId === book.id}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all bg-[#FF453A] text-[#1A1A1A] hover:brightness-110 disabled:opacity-50"
                      >
                        {deletingId === book.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Yes'
                        )}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-1 rounded-lg text-xs font-medium transition-all bg-black/[0.05] text-[#636366] hover:bg-black/[0.08]"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(book.id)}
                      className="p-1.5 rounded-lg text-[#8E8E93] hover:text-[#FF453A] hover:bg-[#FF453A]/10 transition-all"
                      title="Delete book"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MyInProgressBooks;
